import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/utils/toast";
import { FuelType } from "@/types";
import { useTranslation } from "react-i18next";

const combustionFactorSchema = z.object({
  year: z.coerce.number().min(1900).max(2100),
  fuel_type: z.number(),
  coefficient: z.coerce.number().min(0),
});

type CombustionFactorFormValues = z.infer<typeof combustionFactorSchema>;

interface CombustionFactorFormProps {
  id?: number;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FuelTypeResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: FuelType[];
}

export function CombustionFactorForm({
  id,
  onSuccess,
  onCancel,
}: CombustionFactorFormProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  const form = useForm<CombustionFactorFormValues>({
    resolver: zodResolver(combustionFactorSchema),
    defaultValues: {
      year: new Date().getFullYear(),
      fuel_type: undefined,
      coefficient: 0,
    },
  });

  // Fetch fuel types for the select
  const { data: fuelTypesData } = useQuery<FuelTypeResponse>({
    queryKey: ["fuel-types"],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(
          "combustion/types_for_year/?year=" +
            form.getValues("start_date").getFullYear(),
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error(t("coefficients.combustion.error.fetchFuelTypes"));
      }
      return response.json();
    },
    enabled: form.getValues("start_date") !== undefined,
  });

  // Fetch combustion factor data if in edit mode
  const { data: combustionFactorData, isLoading } = useQuery({
    queryKey: ["combustion-factors", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(getApiUrl(`combustion-factors/${id}/`), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(
          t("coefficients.combustion.error.fetchCombustionFactor"),
        );
      }
      return response.json();
    },
    enabled: isEditMode,
  });

  // Set form values when data is loaded
  useEffect(() => {
    if (combustionFactorData) {
      form.reset({
        year: combustionFactorData.year,
        fuel_type: combustionFactorData.fuel_type?.id,
        coefficient: parseFloat(combustionFactorData.coefficient),
      });
    }
  }, [combustionFactorData, form]);

  const mutation = useMutation({
    mutationFn: async (values: CombustionFactorFormValues) => {
      const url = id
        ? getApiUrl(`combustion-factors/${id}/`)
        : getApiUrl("combustion-factors/");
      const method = id ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...values,
          fuel_type: values.fuel_type,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to save combustion factor");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["combustion-factors"] });
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: CombustionFactorFormValues) => {
    mutation.mutate(values);
  };

  if (isLoading && isEditMode) {
    return <div>{t("coefficients.combustion.form.loading")}</div>;
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col h-full"
      >
        <div className="space-y-6 flex-1">
          <FormField
            control={form.control}
            name="year"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("coefficients.combustion.form.year")}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1900}
                    max={2100}
                    {...field}
                    onChange={(e) => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fuel_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("coefficients.combustion.form.fuelType")}
                </FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(parseInt(value))}
                  value={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "coefficients.combustion.form.selectFuelType",
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {fuelTypesData?.results.map((fuelType) => (
                      <SelectItem
                        key={fuelType.id}
                        value={fuelType.id.toString()}
                      >
                        {fuelType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="coefficient"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("coefficients.combustion.form.coefficient")}
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    {...field}
                    onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="fixed inset-x-0 bottom-0 bg-background p-4 border-t flex justify-end space-x-2 mt-8">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={mutation.isPending}
          >
            {t("coefficients.combustion.form.cancel")}
          </Button>
          <Button variant="default" type="submit" loading={mutation.isPending}>
            {isEditMode
              ? t("coefficients.combustion.form.update")
              : t("coefficients.combustion.form.create")}
          </Button>
        </div>
      </form>
    </Form>
  );
}
